import React, { useState, useEffect } from 'react';
import Settings from './Settings';
import ThemeSelector from './ThemeSelector';
import { useTheme } from '../src/hooks/useTheme';
import type { 
  Wine, 
  ShopProfile, 
  AppData 
} from '../types';

interface AdminPageProps {
  onBack: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddWine: (newWine: Omit<Wine, 'id' | 'image'> & { image?: string }) => void;
  onRemoveWine: (wineIdToRemove: number) => void;
  onStartEdit: (wineToEdit: Wine) => void;
  onToggleWineVisibility: (wineIdToToggle: number) => void;
  csvFileName: string | null;
  csvError: string | null;
  wineList: Wine[];
  occasions: string[];
  onAddOccasion: (occasion: string) => void;
  onRemoveOccasion: (occasion: string) => void;
  tastes: string[];
  onAddTaste: (taste: string) => void;
  onRemoveTaste: (taste: string) => void;
  prices: string[];
  onAddPrice: (price: string) => void;
  onRemovePrice: (price: string) => void;
  origins: string[];
  onAddOrigin: (origin: string) => void;
  onRemoveOrigin: (origin: string) => void;
  onImportData: (data: AppData) => Promise<boolean>;
  shopProfile: ShopProfile;
  onUpdateShopProfile: (profileUpdate: Partial<ShopProfile>) => void;
  userId?: string;
}

const AdminPage: React.FC<AdminPageProps> = ({
  onBack,
  userId,
  ...settingsProps
}) => {
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const { theme: currentThemeId } = useTheme();
  
  // テーマが変更されたときのハンドラー
  const handleThemeChange = (themeId: string) => {
    setShowThemeSelector(false);
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* ヘッダー */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">管理者設定</h1>
          
          {/* テーマ選択ボタン */}
          <button
            onClick={() => setShowThemeSelector(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center gap-2"
          >
            🎨 テーマを変更（{currentThemeId}）
          </button>
        </div>
        
        <button
          onClick={onBack}
          className="w-full sm:w-auto bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          お客様ページに戻る
        </button>
      </div>

      {/* 設定画面の内容 */}
      <Settings {...settingsProps} onBack={onBack} userId={userId} />
      
      {/* テーマ選択モーダル */}
      {showThemeSelector && (
        <ThemeSelector 
          onClose={() => setShowThemeSelector(false)}
          onThemeChange={handleThemeChange}
        />
      )}
    </div>
  );
};

export default AdminPage;

